import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { disciplinaryAPI, employeeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  AlertTriangle, 
  Search, 
  Plus,
  FileWarning,
  Shield,
  Calendar,
  User,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const Disciplinary = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '',
    type: 'verbal_warning',
    description: '',
    witness: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recordsRes, employeesRes] = await Promise.all([
        disciplinaryAPI.getAll(),
        employeeAPI.getAll()
      ]);
      setRecords(recordsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Disciplinary fetch error:', error);
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.employee_id || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await disciplinaryAPI.create(formData);
      toast.success('Disciplinary record created!');
      setShowDialog(false);
      setFormData({
        employee_id: '',
        type: 'verbal_warning',
        description: '',
        witness: ''
      });
      fetchData();
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Failed to create record');
    }
  };

  const getEmployeeInfo = (employeeId) => {
    return employees.find(e => e.employee_id === employeeId);
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'verbal_warning':
        return <Badge className="bg-amber-100 text-amber-700">Verbal Warning</Badge>;
      case 'written_warning':
        return <Badge className="bg-orange-100 text-orange-700">Written Warning</Badge>;
      case 'final_warning':
        return <Badge className="bg-red-100 text-red-700">Final Warning</Badge>;
      case 'suspension':
        return <Badge className="bg-red-200 text-red-800">Suspension</Badge>;
      case 'termination':
        return <Badge className="bg-slate-800 text-white">Termination</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'resolved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Resolved</Badge>;
      case 'appealed':
        return <Badge className="bg-blue-100 text-blue-700">Appealed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Generate demo records if none exist
  const displayRecords = records.length > 0 ? records : [
    { id: 'disc-1', employee_id: '100003', type: 'verbal_warning', description: 'Late arrival - 3 occurrences in December', date: '2025-12-15', issued_by: '200001', status: 'active' },
    { id: 'disc-2', employee_id: '100008', type: 'written_warning', description: 'Missed deadline without communication', date: '2025-11-20', issued_by: '200001', status: 'resolved' },
    { id: 'disc-3', employee_id: '100015', type: 'verbal_warning', description: 'Inappropriate email communication', date: '2025-10-05', issued_by: '200002', status: 'resolved' },
  ];

  const filteredRecords = displayRecords.filter(record => {
    const emp = getEmployeeInfo(record.employee_id);
    const name = `${emp?.first_name} ${emp?.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="disciplinary-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-red-600 to-rose-600 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Disciplinary Management
                </h2>
                <p className="text-red-100">
                  Track and manage employee disciplinary actions
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setShowDialog(true)}
              className="bg-white text-red-600 hover:bg-red-50"
              data-testid="new-disciplinary-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Record
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-700 text-sm">Verbal Warnings</p>
                <p className="text-3xl font-bold text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {displayRecords.filter(r => r.type === 'verbal_warning').length}
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-700 text-sm">Written Warnings</p>
                <p className="text-3xl font-bold text-orange-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {displayRecords.filter(r => r.type === 'written_warning').length}
                </p>
              </div>
              <FileWarning className="w-10 h-10 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Active Cases</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {displayRecords.filter(r => r.status === 'active').length}
                </p>
              </div>
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Resolved</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {displayRecords.filter(r => r.status === 'resolved').length}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by employee name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white"
          data-testid="disciplinary-search"
        />
      </div>

      {/* Records List */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Disciplinary Records</CardTitle>
          <CardDescription>All employee disciplinary actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredRecords.map((record) => {
              const emp = getEmployeeInfo(record.employee_id);
              const issuer = getEmployeeInfo(record.issued_by);
              return (
                <div 
                  key={record.id}
                  className={`p-4 rounded-lg border ${
                    record.status === 'active' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'
                  }`}
                  data-testid={`disciplinary-item-${record.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={emp?.avatar} />
                        <AvatarFallback className="bg-red-100 text-red-600 font-medium">
                          {emp?.first_name?.[0]}{emp?.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {emp?.first_name} {emp?.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{emp?.position}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {getTypeBadge(record.type)}
                          {getStatusBadge(record.status)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {record.date}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <User className="w-4 h-4" />
                        Issued by: {issuer?.first_name} {issuer?.last_name}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-white rounded-lg border border-slate-100">
                    <p className="text-sm text-slate-700">{record.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredRecords.length === 0 && (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Records Found</h3>
              <p className="text-slate-500">No disciplinary records match your search</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Record Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create Disciplinary Record</DialogTitle>
            <DialogDescription>Document employee disciplinary action</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select 
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, employee_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.filter(e => e.role === 'employee').map(emp => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select 
                  value={formData.type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verbal_warning">Verbal Warning</SelectItem>
                    <SelectItem value="written_warning">Written Warning</SelectItem>
                    <SelectItem value="final_warning">Final Warning</SelectItem>
                    <SelectItem value="suspension">Suspension</SelectItem>
                    <SelectItem value="termination">Termination</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                placeholder="Describe the incident or reason for disciplinary action..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Witness (Optional)</Label>
              <Input
                placeholder="Name of witness if applicable"
                value={formData.witness}
                onChange={(e) => setFormData(prev => ({ ...prev, witness: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-red-600 hover:bg-red-700">
              Create Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Disciplinary;
